/**
 * Shared core for deriving "artifacts" — the files an agent wrote during a
 * session — from the tool calls in its transcript.
 *
 * There is no platform-level artifact feed yet (see
 * github/copilot-experiences#12895), so this reconstructs one from tool calls
 * that are already persisted.
 *
 * A session's transcript reaches the UI in two unrelated shapes:
 *   - AHP `ChatState` (quick chat, embedded chat, and AHP-environment sessions)
 *   - Mission Control `SessionEvent[]` (cloud and CLI sessions)
 * Both describe the same underlying tool calls, so everything except the shape
 * of the input lives here and each surface contributes only a thin projection
 * into {@link ToolWriteRecord}. See `derive-artifacts-from-chat-state` and
 * `derive-artifacts-from-session-events`.
 *
 * Deriving from the persisted transcript — rather than tracking writes as they
 * happen — is what makes the list survive a page reload: it is a pure function
 * of history that both the live and the replayed path reproduce identically.
 *
 * Deliberately *not* built on `session.workspace_file_changed`: that event
 * exists only in the CLI and is dropped by Mission Control's event allowlist,
 * so it would populate live and then vanish on reload.
 *
 * Known coverage gaps — this reports files written through *recognised write
 * tools* and cannot see writes that never take that shape:
 *   - `bash`/`write_bash` writes via `>`, `>>`, heredocs, `tee`, `sed -i`,
 *     `mv`/`cp`, or any script the command invokes. Detecting these would mean
 *     parsing an unbounded shell grammar; a wrong path shown in the UI is worse
 *     than an omitted one, so this is intentionally not attempted.
 *   - MCP server tools, whose names and argument schemas are defined by each
 *     server and so cannot be allowlisted ahead of time.
 *   - Writes performed by a sub-agent that does not surface its own tool calls
 *     in this transcript.
 * The list is therefore a high-precision, not exhaustive, view: everything it
 * shows was really written, but a session may have touched more.
 */

import {TOOL_NAMES} from '../../constants/tool-names'

/** Whether a write created a new file or changed an existing one. */
export type ArtifactOperation = 'created' | 'modified'

export interface Artifact {
  /** Absolute path exactly as the agent reported it. Stable identity. */
  absolutePath: string
  /** Path trimmed of the root shared by all artifacts, for display. */
  displayPath: string
  /** Trailing path segment. */
  fileName: string
  /** `displayPath` minus `fileName`, without a trailing separator. */
  directory: string
  /** `created` only when no earlier write to the same path was seen. */
  operation: ArtifactOperation
  /** Tool call id of the most recent successful write. */
  toolCallId: string
  /** Result text of the most recent write, for preview. */
  preview?: string
  /**
   * Full file body, when the tool call carried one.
   *
   * Only whole-file writes (`create`, `str_replace_editor` with
   * `command: 'create'`) include the complete contents in their arguments.
   * Partial edits carry only a fragment, so they leave this unset rather than
   * showing something that looks like the file but is not.
   */
  content?: string
  /** Syntax hint derived from the file extension, for the preview renderer. */
  language: string
}

/**
 * Map a file extension onto the language name the preview renderer expects.
 *
 * Only names that change the rendering are listed: `markdown` and `mermaid`
 * select dedicated renderers, the rest select syntax highlighting where the
 * extension and language name differ.
 */
const LANGUAGE_BY_EXTENSION: Record<string, string> = {
  md: 'markdown',
  markdown: 'markdown',
  mmd: 'mermaid',
  mermaid: 'mermaid',
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sh: 'shell',
  yml: 'yaml',
}

export function languageForPath(path: string): string {
  const fileName = splitSegments(path).at(-1) ?? path
  const extension = fileName.includes('.') ? (fileName.split('.').at(-1) ?? '') : ''
  const lowered = extension.toLowerCase()
  return LANGUAGE_BY_EXTENSION[lowered] ?? lowered
}

/**
 * One settled tool call, normalised away from its transport.
 *
 * `resolveArgs` is a callback rather than a plain object because AHP sends a
 * single-argument tool's input as a bare scalar rather than an object, and only
 * the caller knows how to widen that. `primaryKey` names the argument such a
 * scalar should become.
 */
export interface ToolWriteRecord {
  toolCallId: string
  toolName: string
  resolveArgs: (primaryKey: string) => Record<string, unknown>
  succeeded: boolean
  preview?: string
}

/**
 * `str_replace_editor` multiplexes reads and writes behind a `command`
 * discriminator, so the tool name alone does not imply a write. Only these
 * subcommands mutate a file — notably `view` and `undo_edit` do not.
 */
const STR_REPLACE_EDITOR_WRITE_COMMANDS: Record<string, ArtifactOperation> = {
  create: 'created',
  str_replace: 'modified',
  insert: 'modified',
}

/**
 * Tools that write a single file identified by an explicit `path` argument,
 * mapped to the operation they perform.
 *
 * `apply_patch` is absent because it carries no `path` — its targets are
 * embedded in the patch body and are extracted separately.
 */
const SINGLE_PATH_WRITE_TOOLS: Record<string, ArtifactOperation> = {
  [TOOL_NAMES.CREATE]: 'created',
  [TOOL_NAMES.EDIT]: 'modified',
  [TOOL_NAMES.STR_REPLACE]: 'modified',
  [TOOL_NAMES.INSERT]: 'modified',
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Extract target paths from an `apply_patch` body.
 *
 * Supports the `*** Add/Update/Delete File:` envelope as well as plain unified
 * diff `+++ b/path` headers, since both shapes reach this tool.
 *
 * Paths are captured to end-of-line and trimmed; a repeating separator or lazy
 * capture next to `.*`/`\s*$` backtracks polynomially (CodeQL js/polynomial-redos).
 */
export function parseApplyPatchPaths(content: string): Array<{path: string; operation: ArtifactOperation}> {
  const results: Array<{path: string; operation: ArtifactOperation}> = []

  for (const line of content.split('\n')) {
    const envelope = line.match(/^\*\*\*\s+(Add|Update|Delete)\s+File:(.*)$/)
    if (envelope) {
      // A deletion removes the file, so it is not a browsable artifact.
      if (envelope[1] === 'Delete') continue
      const path = envelope[2]?.trim()
      if (path) results.push({path, operation: envelope[1] === 'Add' ? 'created' : 'modified'})
      continue
    }

    const unified = line.match(/^\+\+\+\s(.*)$/)?.[1]?.trim()
    if (unified === undefined) continue
    const unifiedPath = unified.startsWith('b/') ? unified.slice(2) : unified
    if (unifiedPath && unifiedPath !== '/dev/null') {
      results.push({path: unifiedPath, operation: 'modified'})
    }
  }

  return results
}

interface Write {
  path: string
  operation: ArtifactOperation
  content?: string
}

/** The whole-file body a create-shaped call carries, if any. */
function wholeFileContent(args: Record<string, unknown>): string | undefined {
  const body = args['file_text'] ?? args['content']
  return typeof body === 'string' ? body : undefined
}

/** Every path written by a single tool call, or `[]` if it is not a write. */
function writesFor(record: ToolWriteRecord): Write[] {
  const {toolName} = record

  const singlePathOperation = SINGLE_PATH_WRITE_TOOLS[toolName]
  if (singlePathOperation) {
    const args = record.resolveArgs('path')
    const path = args['path']
    if (typeof path !== 'string' || !path) return []
    const content = singlePathOperation === 'created' ? wholeFileContent(args) : undefined
    return [{path, operation: singlePathOperation, ...(content === undefined ? {} : {content})}]
  }

  if (toolName === TOOL_NAMES.STR_REPLACE_EDITOR) {
    const args = record.resolveArgs('path')
    const command = args['command']
    const operation = typeof command === 'string' ? STR_REPLACE_EDITOR_WRITE_COMMANDS[command] : undefined
    if (!operation) return []
    const path = args['path']
    if (typeof path !== 'string' || !path) return []
    const content = command === 'create' ? wholeFileContent(args) : undefined
    return [{path, operation, ...(content === undefined ? {} : {content})}]
  }

  if (toolName === TOOL_NAMES.APPLY_PATCH) {
    const content = record.resolveArgs('content')['content']
    return typeof content === 'string' ? parseApplyPatchPaths(content) : []
  }

  return []
}

function splitSegments(path: string): string[] {
  return path.split(/[/\\]/).filter(Boolean)
}

/**
 * Number of leading directory segments shared by every path.
 *
 * Purely a display concern: absolute host paths are long and mostly identical,
 * so trimming the shared root keeps the list readable. It is derived from the
 * paths themselves because neither transcript shape carries a workspace root to
 * compare against.
 *
 * At least one directory is always kept, so files never collapse to bare names
 * and stay distinguishable when the same name appears in several directories.
 */
function commonRootSegmentCount(paths: string[]): number {
  const [first, ...rest] = paths
  if (first === undefined) return 0

  const candidate = splitSegments(first).slice(0, -1)
  let shared = candidate.length
  if (shared === 0) return 0

  for (const path of rest) {
    const segments = splitSegments(path)
    let index = 0
    while (index < shared && index < segments.length - 1 && candidate[index] === segments[index]) {
      index++
    }
    shared = index
    if (shared === 0) return 0
  }

  // Leave every path at least one directory of context.
  for (const path of paths) {
    shared = Math.min(shared, splitSegments(path).length - 2)
  }
  return Math.max(shared, 0)
}

/**
 * Reduce normalised tool calls to the files they wrote, in first-write order.
 *
 * Writes are deduplicated by absolute path keeping the most recent one, so a
 * file edited repeatedly appears once. A path first seen as `created` stays
 * `created` even after later edits. Failed and in-flight calls are ignored.
 */
export function collectArtifacts(records: Iterable<ToolWriteRecord>): Artifact[] {
  // Insertion order is first-write order, which `Map` preserves across updates.
  const byPath = new Map<string, Artifact>()

  for (const record of records) {
    if (!record.succeeded) continue

    for (const write of writesFor(record)) {
      const existing = byPath.get(write.path)
      // A later partial edit must not drop the body an earlier create captured.
      const content = write.content ?? existing?.content
      byPath.set(write.path, {
        absolutePath: write.path,
        // Both are finalised below, once the whole set is known.
        displayPath: write.path,
        directory: '',
        fileName: splitSegments(write.path).at(-1) ?? write.path,
        // A file created earlier in the session stays "created".
        operation: existing?.operation === 'created' ? 'created' : write.operation,
        toolCallId: record.toolCallId,
        language: languageForPath(write.path),
        ...(record.preview === undefined ? {} : {preview: record.preview}),
        ...(content === undefined ? {} : {content}),
      })
    }
  }

  const artifacts = [...byPath.values()]
  const rootSegments = commonRootSegmentCount(artifacts.map(artifact => artifact.absolutePath))

  return artifacts.map(artifact => {
    const segments = splitSegments(artifact.absolutePath).slice(rootSegments)
    return {
      ...artifact,
      displayPath: segments.join('/') || artifact.fileName,
      directory: segments.slice(0, -1).join('/'),
    }
  })
}
